/**
 * Seeded Random Number Generator
 * 
 * Provides deterministic randomness for repeatable simulations.
 * Uses a Mulberry32 algorithm for fast, high-quality pseudo-random numbers.
 * 
 * The same seed will ALWAYS produce the same sequence of random numbers.
 */

export interface SeededRNG {
  /** Generate a random float in [0, 1) */
  random(): number;
  /** Generate a random integer in [min, max] (inclusive) */
  randomInt(min: number, max: number): number;
  /** Generate a random float in [min, max) */
  randomFloat(min: number, max: number): number;
  /** Pick a random element from an array */
  pick<T>(array: T[]): T | undefined;
  /** Pick N random elements from an array (without replacement) */
  pickN<T>(array: T[], n: number): T[];
  /** Shuffle an array in place and return it */
  shuffle<T>(array: T[]): T[];
  /** Select based on weighted probabilities */
  weightedPick<T>(items: T[], weights: number[]): T | undefined;
  /** Generate a random boolean with given probability of true */
  randomBool(probability?: number): boolean;
  /** Generate a normally distributed random number (Box-Muller) */
  randomNormal(mean?: number, stdDev?: number): number;
  /** Get current seed value */
  getSeed(): number;
  /** Fork this RNG with a new seed derived from current state */
  fork(): SeededRNG;
}

/**
 * Mulberry32 PRNG - fast and high-quality 32-bit generator
 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0; // Ensure unsigned 32-bit
  
  return function(): number {
    state |= 0;
    state = (state + 0x6D2B79F5) | 0;
    let t = Math.imul(state ^ (state >>> 15), state | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Create a seeded random number generator
 * @param seed - Integer seed value. Same seed = same sequence.
 */
export function createSeededRNG(seed: number): SeededRNG {
  const nextRandom = mulberry32(seed);
  
  // Store for Box-Muller spare
  let hasSpare = false;
  let spare = 0;
  
  const rng: SeededRNG = {
    random(): number {
      return nextRandom();
    },
    
    randomInt(min: number, max: number): number {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(nextRandom() * (max - min + 1)) + min;
    },
    
    randomFloat(min: number, max: number): number {
      return nextRandom() * (max - min) + min;
    },
    
    pick<T>(array: T[]): T | undefined {
      if (array.length === 0) return undefined;
      const index = Math.floor(nextRandom() * array.length);
      return array[index];
    },
    
    pickN<T>(array: T[], n: number): T[] {
      if (n >= array.length) return rng.shuffle([...array]);
      const copy = [...array];
      const result: T[] = [];
      for (let i = 0; i < n && copy.length > 0; i++) {
        const index = Math.floor(nextRandom() * copy.length);
        result.push(copy.splice(index, 1)[0]);
      }
      return result;
    },
    
    shuffle<T>(array: T[]): T[] {
      // Fisher-Yates shuffle
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(nextRandom() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    
    weightedPick<T>(items: T[], weights: number[]): T | undefined {
      if (items.length === 0 || weights.length === 0) return undefined;
      
      const totalWeight = weights.reduce((sum, w) => sum + Math.max(0, w), 0);
      if (totalWeight === 0) return rng.pick(items);
      
      let random = nextRandom() * totalWeight;
      for (let i = 0; i < items.length; i++) {
        random -= Math.max(0, weights[i]);
        if (random <= 0) return items[i];
      }
      return items[items.length - 1];
    },
    
    randomBool(probability: number = 0.5): boolean {
      return nextRandom() < probability;
    },
    
    randomNormal(mean: number = 0, stdDev: number = 1): number {
      // Box-Muller transform
      if (hasSpare) {
        hasSpare = false;
        return mean + stdDev * spare;
      }
      
      let u: number, v: number, s: number;
      do {
        u = nextRandom() * 2 - 1;
        v = nextRandom() * 2 - 1;
        s = u * u + v * v;
      } while (s >= 1 || s === 0);
      
      const mul = Math.sqrt(-2.0 * Math.log(s) / s);
      spare = v * mul;
      hasSpare = true;
      
      return mean + stdDev * u * mul;
    },
    
    getSeed(): number {
      return seed;
    },
    
    fork(): SeededRNG {
      // Create a new RNG with a seed derived from current state
      const newSeed = Math.floor(nextRandom() * 0xFFFFFFFF);
      return createSeededRNG(newSeed);
    },
  };
  
  return rng;
}

/**
 * Create a seed from a string (for named scenarios)
 */
export function stringToSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate a random seed (for non-deterministic runs)
 */
export function generateRandomSeed(): number {
  return Math.floor(Math.random() * 0xFFFFFFFF);
}
